//
// Copyright © 2022-2023 Hardcore Engineering Inc.
//
// Licensed under the Eclipse Public License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License. You may
// obtain a copy of the License at https://www.eclipse.org/legal/epl-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
//
// See the License for the specific language governing permissions and
// limitations under the License.
//

import { Employee, Person } from '@hcengineering/contact'
import {
  AttachedDoc,
  Attribute,
  Class,
  MarkupBlobRef,
  CollectionSize,
  Data,
  Doc,
  Markup,
  Mixin,
  Ref,
  RelatedDocument,
  Space,
  Status,
  Timestamp,
  Type,
  type Permission
} from '@hcengineering/core'
import { Asset, IntlString, Metadata, Plugin, Resource, plugin } from '@hcengineering/platform'
import { Preference } from '@hcengineering/preference'
import { TagCategory, TagElement, TagReference } from '@hcengineering/tags'
import { ToDo } from '@hcengineering/time'
import {
  ProjectType,
  ProjectTypeDescriptor,
  Task,
  Project as TaskProject,
  TaskStatusFactory,
  TaskType,
  TaskTypeDescriptor
} from '@hcengineering/task'
import { AnyComponent, ComponentExtensionId, Location, ResolvedLocation } from '@hcengineering/ui'
import { Action, ActionCategory, IconProps } from '@hcengineering/view'

export * from './analytics'

/**
 * @public
 */
export interface IssueStatus extends Status {}

/**
 * @public
 */
export interface Project extends TaskProject, IconProps {
  identifier: string // Project identifier
  sequence: number
  defaultIssueStatus?: Ref<IssueStatus>
  defaultAssignee?: Ref<Employee>
  defaultTimeReportDay: TimeReportDayType

  // Optional: only projects that were ever opened in the AI task agent carry this counter.
  aiTaskSessions?: CollectionSize<AiTaskChatSession>
}

/**
 * @public
 */
export interface ProjectTargetPreference extends Preference {
  attachedTo: Ref<Project> // tracker.ids.ProjectPreferences

  usedOn: Timestamp

  props?: { key: string, value: any }[]
}

export type RelatedIssueKind = 'classRule' | 'spaceRule'

export interface RelatedClassRule {
  kind: 'classRule'
  ofClass: Ref<Class<Doc>>
}

export interface RelatedSpaceRule {
  kind: 'spaceRule'
  space: Ref<Space>
}

/**
 * @public
 *
 * If defined, will be used to set a default project for this kind of document's related issues.
 */
export interface RelatedIssueTarget extends Doc {
  // Attached to project.
  target?: Ref<Project> | null
  rule: RelatedClassRule | RelatedSpaceRule
}

/**
 * @public
 */
export enum TimeReportDayType {
  CurrentWorkDay = 'CurrentWorkDay',
  PreviousWorkDay = 'PreviousWorkDay'
}

/**
 * @public
 */
export enum IssuePriority {
  NoPriority,
  Urgent,
  High,
  Medium,
  Low
}

/**
 * Dependency kind between two Issues for Gantt scheduling.
 *
 * - `finish-to-start` (FS): A must finish before B can start. Most common.
 * - `start-to-start` (SS): A must start before B can start.
 * - `finish-to-finish` (FF): A must finish before B can finish.
 * - `start-to-finish` (SF): A must start before B can finish. Rare.
 *
 * @public
 */
export type DependencyKind = 'finish-to-start' | 'start-to-start' | 'finish-to-finish' | 'start-to-finish'

/**
 * @public
 */
export enum IssuesGrouping {
  Status = 'status',
  Assignee = 'assignee',
  Priority = 'priority',
  Component = 'component',
  Milestone = 'milestone',
  NoGrouping = '#no_category'
}

/**
 * @public
 */
export enum IssuesOrdering {
  Status = 'status',
  Priority = 'priority',
  LastUpdated = 'modifiedOn',
  DueDate = 'dueDate',
  Manual = 'rank'
}

/**
 * @public
 */
export enum IssuesDateModificationPeriod {
  All = 'all',
  PastWeek = 'pastWeek',
  PastMonth = 'pastMonth'
}

/**
 * @public
 */
export enum MilestoneStatus {
  Planned,
  InProgress,
  Completed,
  Canceled
}

/**
 * @public
 */
export interface Milestone extends Doc {
  label: string
  description?: Markup

  status: MilestoneStatus

  space: Ref<Project>

  comments: number
  attachments?: number

  startDate: Timestamp | null // null = open-ended begin marker
  targetDate: Timestamp
}

/**
 * @public
 */
export interface Issue extends Task {
  attachedTo: Ref<Issue>
  title: string
  description: MarkupBlobRef | null
  status: Ref<IssueStatus>
  priority: IssuePriority

  component: Ref<Component> | null

  // For subtasks
  subIssues: CollectionSize<Issue>
  blockedBy?: RelatedDocument[]
  relations?: RelatedDocument[]
  parents: IssueParentInfo[]

  startDate: Timestamp | null // for Gantt scheduling; null = unscheduled

  space: Ref<Project>

  milestone?: Ref<Milestone> | null

  // Estimation in man hours
  estimation: number

  // Remaining time in man hours
  remainingTime: number

  // ReportedTime time, auto updated using trigger.
  reportedTime: number
  // Collection of reportedTime entries, for proper time estimations per person.
  reports: CollectionSize<TimeSpendReport>

  childInfo: IssueChildInfo[]

  template?: {
    // A template issue is based on
    template: Ref<IssueTemplate>
    // Child id in template
    childId?: string
  }

  todos?: CollectionSize<ToDo>

  // Optional: only issues that were ever opened in the AI agent carry this counter.
  aiSessions?: CollectionSize<AiChatSession>
}

/**
 * @public
 */
export type AiChatRole = 'user' | 'assistant'

/**
 * @public
 *
 * A single AI agent conversation attached to an issue.
 *
 * The space is the issue's project space (not a PersonSpace) so that the history belongs to the
 * issue and is shared by every Owner instead of being fragmented per author. Confidentiality is
 * therefore *not* provided by the space: it is enforced server side by AiOwnerOnlyMiddleware,
 * which is the only thing that keeps DocGuest/ReadOnlyGuest tokens (any "Public link") from
 * reading this domain.
 */
export interface AiChatSession extends AttachedDoc<Issue, 'aiSessions'> {
  // Model the session was started with. A session may switch models mid-way, so every message
  // also records the tokens it actually consumed.
  model: string

  messages: CollectionSize<AiChatMessage>

  totalInputTokens: number
  totalOutputTokens: number
  totalReasoningTokens: number
  totalCachedTokens: number

  // Integer micro-dollars (1e-6 USD), never a float: the totals are accumulated with `$inc`, and
  // a float would both drift over a long session and lose concurrent increments.
  totalCostMicroUsd: number
}

/**
 * @public
 *
 * One turn of an AiChatSession. Stored as plain text, never Markup: the payload comes from and
 * goes back to a text API, and converting it twice would only burn tokens and space.
 */
export interface AiChatMessage extends AttachedDoc<AiChatSession, 'messages'> {
  role: AiChatRole

  content: string
  // Reasoning summary streamed by the model, when it produced one.
  thinking?: string
  // JSON-serialized sub-issue proposals. Kept opaque here so the shape can evolve in the agent
  // without a model migration.
  proposals?: string
  // Whether the proposals of this message were already turned into sub-issues.
  applied: boolean
  // Name of the document the user attached to this turn. The file itself is never persisted.
  documentName?: string

  inputTokens?: number
  outputTokens?: number
  reasoningTokens?: number
  cachedTokens?: number
  // Same unit as AiChatSession.totalCostMicroUsd: integer micro-dollars.
  costMicroUsd?: number
}

/**
 * @public
 *
 * A single AI TASK agent conversation, attached to a PROJECT.
 *
 * Sibling of AiChatSession, not a generalisation of it: the two agents are separate products that
 * happen to share a shape today, and a common harness is expected to unify them later. Keeping the
 * classes apart is what lets the sub-issue agent stay untouched while this one moves.
 *
 * A Project is a Space, which changes nothing here: AttachedDoc accepts any Doc as parent, the
 * derived `$inc` of the collection counter runs (Project lives in DOMAIN_SPACE, not DOMAIN_MODEL)
 * and the cascade delete works (deleteClassCollections walks the Collection attributes of any
 * removed class).
 *
 * The space is the project itself, so the history belongs to the project and every Owner sees the
 * same one. That space is an ordinary project space, so confidentiality is *not* provided by it:
 * it comes from AiOwnerOnlyMiddleware, which guards the whole DOMAIN_AI_CHAT domain and therefore
 * covers this class without a line of server change.
 */
export interface AiTaskChatSession extends AttachedDoc<Project, 'aiTaskSessions'> {
  // Model the session was started with. A session may switch models mid-way, so every message
  // also records the tokens it actually consumed.
  model: string

  messages: CollectionSize<AiTaskChatMessage>

  totalInputTokens: number
  totalOutputTokens: number
  totalReasoningTokens: number
  totalCachedTokens: number

  // Integer micro-dollars (1e-6 USD), never a float: the totals are accumulated with `$inc`, and
  // a float would both drift over a long session and lose concurrent increments.
  totalCostMicroUsd: number
}

/**
 * @public
 *
 * One turn of an AiTaskChatSession. Stored as plain text, never Markup, for the same reason as
 * AiChatMessage: the payload comes from and goes back to a text API.
 */
export interface AiTaskChatMessage extends AttachedDoc<AiTaskChatSession, 'messages'> {
  role: AiChatRole

  content: string
  // Reasoning summary streamed by the model, when it produced one.
  thinking?: string
  // JSON-serialized task proposals. Kept opaque here so the shape can evolve in the agent without
  // a model migration.
  proposals?: string
  // Whether the proposals of this message were already turned into issues.
  applied: boolean
  // Identifiers of the issues that were actually created (e.g. RENOTE-42). Optional because a
  // message written before the batch ran has none — and because after a batch that stopped halfway
  // this is the only record of WHICH demands got in, which is what a retry has to avoid repeating.
  appliedIdentifiers?: string[]
  // Name of the document the user attached to this turn. The file itself is never persisted.
  documentName?: string

  inputTokens?: number
  outputTokens?: number
  reasoningTokens?: number
  cachedTokens?: number
  // Same unit as AiTaskChatSession.totalCostMicroUsd: integer micro-dollars.
  costMicroUsd?: number
}

/**
 * @public
 */
export interface IssueDraft {
  kind: Ref<TaskType>
  _id: Ref<Issue>
  title: string
  description: Markup
  status?: Ref<IssueStatus>
  priority: IssuePriority
  assignee: Ref<Person> | null
  component: Ref<Component> | null
  space: Ref<Project>
  startDate: Timestamp | null
  dueDate: Timestamp | null
  milestone?: Ref<Milestone> | null

  // Estimation in man days
  estimation: number
  parentIssue?: Ref<Issue>
  attachments?: number
  labels: TagReference[]
  subIssues: IssueDraft[]
  template?: {
    // A template issue is based on
    template: Ref<IssueTemplate>
    // Child id in template
    childId?: string
  }
}

/**
 * @public
 */
export interface IssueTemplateData {
  title: string
  description: Markup
  priority: IssuePriority

  assignee: Ref<Person> | null
  component: Ref<Component> | null

  milestone?: Ref<Milestone> | null

  // Estimation in man days
  estimation: number

  labels?: Ref<TagElement>[]

  kind?: Ref<TaskType>
}

/**
 * @public
 */
export interface IssueTemplateChild extends IssueTemplateData {
  id: Ref<Issue>
}

/**
 * @public
 */
export interface IssueTemplate extends Doc, IssueTemplateData {
  space: Ref<Project>

  children: IssueTemplateChild[]

  // Discussion stuff
  comments: number
  attachments?: number

  relations?: RelatedDocument[]
}

/**
 * @public
 *
 * Declares time spend entry
 */
export interface TimeSpendReport extends AttachedDoc {
  attachedTo: Ref<Issue>

  employee: Ref<Employee> | null

  date: Timestamp | null
  // Value in man hours
  value: number

  description: string
}

/**
 * @public
 */
export interface IssueParentInfo {
  parentId: Ref<Issue>
  identifier: string
  parentTitle: string
  space: Ref<Space>
}

/**
 * Typed dependency between two Issues, used by the Gantt view to compute
 * cascade scheduling and critical path.
 *
 * Persisted as an AttachedDoc collection 'relations' on the source Issue.
 *
 * @public
 */
export interface IssueRelation extends AttachedDoc<Issue, 'relations'> {
  target: Ref<Issue> // successor
  kind: DependencyKind
  /** Lag in schedule days; can be negative (overlap). */
  lag: number
}

/**
 * @public
 */
export interface IssueChildInfo {
  childId: Ref<Issue>
  estimation: number
  reportedTime: number
}

/**
 * @public
 */
export interface Document extends Doc {
  title: string
  icon: string | null
  color: number
  content?: Markup

  space: Ref<Project>
}

/**
 * @public
 */
export interface Component extends Doc {
  label: string
  description?: Markup
  lead: Ref<Employee> | null
  space: Ref<Project>
  comments: number
  attachments?: number
}

/**
 * @public
 */
export const trackerId = 'tracker' as Plugin
export * from './analytics'

const pluginState = plugin(trackerId, {
  class: {
    Project: '' as Ref<Class<Project>>,
    Issue: '' as Ref<Class<Issue>>,
    IssueRelation: '' as Ref<Class<IssueRelation>>,
    IssueTemplate: '' as Ref<Class<IssueTemplate>>,
    Component: '' as Ref<Class<Component>>,
    IssueStatus: '' as Ref<Class<IssueStatus>>,
    TypeIssuePriority: '' as Ref<Class<Type<IssuePriority>>>,
    Milestone: '' as Ref<Class<Milestone>>,
    TypeMilestoneStatus: '' as Ref<Class<Type<MilestoneStatus>>>,
    TimeSpendReport: '' as Ref<Class<TimeSpendReport>>,
    TypeReportedTime: '' as Ref<Class<Type<number>>>,
    TypeEstimation: '' as Ref<Class<Type<number>>>,
    TypeRemainingTime: '' as Ref<Class<Type<number>>>,
    RelatedIssueTarget: '' as Ref<Class<RelatedIssueTarget>>,
    ProjectTargetPreference: '' as Ref<Class<ProjectTargetPreference>>,
    AiChatSession: '' as Ref<Class<AiChatSession>>,
    AiChatMessage: '' as Ref<Class<AiChatMessage>>,
    // Task agent. Separate classes, same domain (ai_chat) — that is what puts them under
    // AiOwnerOnlyMiddleware without touching the server.
    AiTaskChatSession: '' as Ref<Class<AiTaskChatSession>>,
    AiTaskChatMessage: '' as Ref<Class<AiTaskChatMessage>>
  },
  mixin: {
    ClassicProjectTypeData: '' as Ref<Mixin<Project>>,
    IssueTypeData: '' as Ref<Mixin<Issue>>
  },
  ids: {
    NoParent: '' as Ref<Issue>,
    IssueDraft: '',
    IssueDraftChild: '',
    ClassingProjectType: '' as Ref<ProjectType>,
    // Typed as Ref<Doc> and not Ref<Widget>: this package does not depend on
    // @hcengineering/workbench, so the consumer casts it (same trick as card.ids.CardWidget).
    AiAgentWidget: '' as Ref<Doc>,
    // Second widget, for the task agent. A widget of its own, not a second tab kind on the one
    // above: opening one agent must never close the other's tab.
    AiTaskAgentWidget: '' as Ref<Doc>
  },
  status: {
    Backlog: '' as Ref<Status>,
    Todo: '' as Ref<Status>,
    InProgress: '' as Ref<Status>,
    Coding: '' as Ref<Status>,
    UnderReview: '' as Ref<Status>,
    Done: '' as Ref<Status>,
    Canceled: '' as Ref<Status>
  },
  component: {
    Tracker: '' as AnyComponent,
    TrackerApp: '' as AnyComponent,
    RelatedIssues: '' as AnyComponent,
    RelatedIssuesSection: '' as AnyComponent,
    RelatedIssueSelector: '' as AnyComponent,
    RelatedIssueTemplates: '' as AnyComponent,
    EditIssue: '' as AnyComponent,
    CreateIssue: '' as AnyComponent,
    ProjectPresenter: '' as AnyComponent,
    CreateIssueTemplate: '' as AnyComponent,
    CreateProject: '' as AnyComponent,
    IssueStatusPresenter: '' as AnyComponent,
    LabelsView: '' as AnyComponent,
    AiAgentPanel: '' as AnyComponent,
    AiAgentButton: '' as AnyComponent,
    AiTaskAgentPanel: '' as AnyComponent,
    AiTaskAgentButton: '' as AnyComponent
  },
  attribute: {
    IssueStatus: '' as Ref<Attribute<Status>>
  },
  icon: {
    TrackerApplication: '' as Asset,
    Component: '' as Asset,
    Issue: '' as Asset,
    Subissue: '' as Asset,
    Project: '' as Asset,
    Relations: '' as Asset,
    Inbox: '' as Asset,
    MyIssues: '' as Asset,
    Views: '' as Asset,
    Issues: '' as Asset,
    Components: '' as Asset,
    NewIssue: '' as Asset,
    Magnifier: '' as Asset,
    Labels: '' as Asset,
    DueDate: '' as Asset,
    Parent: '' as Asset,
    UnsetParent: '' as Asset,
    Milestone: '' as Asset,
    IssueTemplates: '' as Asset,
    Start: '' as Asset,
    Stop: '' as Asset,

    CategoryBacklog: '' as Asset,
    CategoryUnstarted: '' as Asset,
    CategoryStarted: '' as Asset,
    CategoryCompleted: '' as Asset,
    CategoryCanceled: '' as Asset,

    PriorityNoPriority: '' as Asset,
    PriorityUrgent: '' as Asset,
    PriorityHigh: '' as Asset,
    PriorityMedium: '' as Asset,
    PriorityLow: '' as Asset,

    ComponentsList: '' as Asset,

    MilestoneStatusPlanned: '' as Asset,
    MilestoneStatusInProgress: '' as Asset,
    MilestoneStatusPaused: '' as Asset,
    MilestoneStatusCompleted: '' as Asset,
    MilestoneStatusCanceled: '' as Asset,

    CopyBranch: '' as Asset,
    Duplicate: '' as Asset,

    TimeReport: '' as Asset,
    Estimation: '' as Asset,

    // Project icons
    Home: '' as Asset,
    RedCircle: '' as Asset
  },
  image: {
    // Raster brand art, one per theme. It is deliberately NOT under `icon`: Icon.svelte renders an
    // Asset as `<svg><use href/></svg>`, which silently draws nothing for a bitmap URL. These are
    // consumed with `getMetadata()` inside an <img>, the same way contact.image.ProfileBackground is.
    AiAgentDark: '' as Asset,
    AiAgentLight: '' as Asset
  },
  metadata: {
    // Base URL of the sub-issue agent pod, fed from AGENT_URL in the front's config.json.
    // Empty when the deployment has no agent: every caller treats that as "feature off".
    AiAgentUrl: '' as Metadata<string>
  },
  category: {
    Other: '' as Ref<TagCategory>,
    Tracker: '' as Ref<ActionCategory>
  },
  descriptors: {
    ProjectType: '' as Ref<ProjectTypeDescriptor>,
    Issue: '' as Ref<TaskTypeDescriptor>
  },
  action: {
    CopyAsMarkdownTable: '' as Ref<Action<Doc, any>>,
    SetDueDate: '' as Ref<Action<Doc, any>>,
    SetParent: '' as Ref<Action<Doc, any>>,
    SetStatus: '' as Ref<Action>,
    SetPriority: '' as Ref<Action<Doc, any>>,
    SetAssignee: '' as Ref<Action<Doc, any>>,
    SetComponent: '' as Ref<Action<Doc, any>>,
    CopyIssueId: '' as Ref<Action<Doc, any>>,
    CopyIssueTitle: '' as Ref<Action<Doc, any>>,
    CopyIssueLink: '' as Ref<Action<Doc, any>>,
    MoveToProject: '' as Ref<Action>,
    Duplicate: '' as Ref<Action<Doc, any>>,
    Relations: '' as Ref<Action<Doc, any>>,
    NewIssue: '' as Ref<Action<Doc, any>>,
    NewIssueGlobal: '' as Ref<Action<Doc, any>>,
    NewSubIssue: '' as Ref<Action<Doc, any>>,
    EditWorkflowStatuses: '' as Ref<Action>,
    EditProject: '' as Ref<Action>,
    SetMilestone: '' as Ref<Action<Doc, any>>,
    SetLabels: '' as Ref<Action<Doc, any>>,
    EditRelatedTargets: '' as Ref<Action<Doc, any>>,
    UnsetParent: '' as Ref<Action<Doc, any>>
  },
  project: {
    DefaultProject: '' as Ref<Project>
  },
  resolver: {
    Location: '' as Resource<(loc: Location) => Promise<ResolvedLocation | undefined>>
  },
  string: {
    TrackerApplication: '' as IntlString,
    ConfigLabel: '' as IntlString,
    NewRelatedIssue: '' as IntlString,
    IssueNotificationTitle: '' as IntlString,
    IssueNotificationBody: '' as IntlString,
    IssueNotificationChanged: '' as IntlString,
    IssueNotificationChangedProperty: '' as IntlString,
    IssueNotificationMessage: '' as IntlString,
    IssueAssignedToYou: '' as IntlString,
    Project: '' as IntlString,
    RelatedIssues: '' as IntlString,
    Issue: '' as IntlString,
    IssueStartDate: '' as IntlString,
    GanttDependency: '' as IntlString,
    GanttLag: '' as IntlString,
    NewProject: '' as IntlString,
    UnsetParentIssue: '' as IntlString,
    ForbidCreateProjectPermission: '' as IntlString,
    ForbidCreateProjectPermissionDescription: '' as IntlString,

    // AI sub-issue agent
    AiAgent: '' as IntlString,
    AiAgentTitle: '' as IntlString,
    AiAgentTooltip: '' as IntlString,
    AiSessions: '' as IntlString,
    AiMessages: '' as IntlString,
    AiModel: '' as IntlString,
    AiMessagePlaceholder: '' as IntlString,
    AiAttachDocument: '' as IntlString,
    AiDocumentAttached: '' as IntlString,
    AiThinking: '' as IntlString,
    AiWaiting: '' as IntlString,
    AiCreating: '' as IntlString,
    AiCreated: '' as IntlString,
    AiStop: '' as IntlString,
    AiError: '' as IntlString,
    AiProposals: '' as IntlString,
    // Was missing while AiProposalCard.svelte already used it and both lang files already carried
    // the text, so the sub-issue agent's duplicate warning resolved to `undefined` and rendered as
    // an empty line. Only svelte-check sees this class of mistake — a .ts consumer would not
    // compile, but a .svelte one just draws nothing.
    AiDuplicateOf: '' as IntlString,
    AiApprove: '' as IntlString,
    AiDiscard: '' as IntlString,
    AiApplied: '' as IntlString,
    AiTokens: '' as IntlString,
    AiCost: '' as IntlString,
    AiSessionCost: '' as IntlString,
    AiOwnerOnly: '' as IntlString,
    AiNoDocumentText: '' as IntlString,

    // Transport failures of EITHER agent, in the user's language. These used to be English literals
    // built in the browser and rendered raw in the transcript, so a pt-BR user read "network error".
    AiNotConfigured: '' as IntlString,
    AiNetworkError: '' as IntlString,
    AiStreamInterrupted: '' as IntlString,
    AiHttpError: '' as IntlString,

    // AI agent for the DEMANDS OF A PROJECT.
    //
    // Vocabulary note, because the identifiers say "task" and the strings say "demanda": what this
    // agent creates is `tracker.class.Issue` at the ROOT of a project (ai-tasks/apply.ts), which is
    // the very object the "New demand" button creates and which the whole pt-BR UI calls
    // "Demanda" (feminine). There is no separate "tarefa" entity — the only legitimate "tarefa" in
    // this domain is the TaskType, the KIND of a demand. So the user-visible wording is
    // demanda/demandas, while the code identifiers (ai-tasks/, AiTaskChatSession, propose_tasks)
    // stay as they are: they are persisted in `_class` and on the wire, and renaming them after the
    // first workspace uses the agent would cost a data migration for zero user-visible gain.
    //
    // Only the strings whose WORDING differs from the sub-issue agent's are declared here; the
    // neutral ones (AiModel, AiThinking, AiWaiting, AiStop, AiError, AiDiscard, AiTokens, AiCost,
    // AiSessionCost, AiOwnerOnly, AiAttachDocument, AiDocumentAttached, AiNoDocumentText) are
    // reused as they are.
    AiTaskAgent: '' as IntlString,
    AiTaskAgentTitle: '' as IntlString,
    AiTaskAgentTooltip: '' as IntlString,
    AiTaskSessions: '' as IntlString,
    AiTaskMessages: '' as IntlString,
    AiTaskMessagePlaceholder: '' as IntlString,
    AiTaskProposals: '' as IntlString,
    AiTaskDuplicateOf: '' as IntlString,
    AiTaskApprove: '' as IntlString,
    AiTaskCreating: '' as IntlString,
    AiTaskCreated: '' as IntlString,
    AiTaskApplied: '' as IntlString,
    AiTaskNoTaskType: '' as IntlString,
    AiTaskNoStatus: '' as IntlString,
    AiTaskCancelled: '' as IntlString,
    AiTaskPartialFailure: '' as IntlString,
    AiTaskWidgetUnavailable: '' as IntlString
  },
  extensions: {
    IssueListHeader: '' as ComponentExtensionId,
    EditIssueHeader: '' as ComponentExtensionId,
    EditIssueTitle: '' as ComponentExtensionId
  },
  taskTypes: {
    Issue: '' as Ref<TaskType>,
    SubIssue: '' as Ref<TaskType>
  },
  permission: {
    ForbidCreateProject: '' as Ref<Permission>
  }
})
export default pluginState

/**
 * @public
 */
export function createStatesData (data: TaskStatusFactory[]): Omit<Data<Status>, 'rank'>[] {
  const states: Omit<Data<Status>, 'rank'>[] = []

  for (const category of data) {
    for (const sName of category.statuses) {
      states.push({
        ofAttribute: pluginState.attribute.IssueStatus,
        name: Array.isArray(sName) ? sName[0] : sName,
        color: Array.isArray(sName) ? sName[1] : undefined,
        category: category.category
      })
    }
  }
  return states
}
